import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Control, Controller, FieldPath, FieldValues, useForm, useWatch } from 'react-hook-form';
import cn from 'clsx';
import reviewStyles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import styles from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { RiskService } from '@utils/risk.service';

export type ReviewFieldsType = {
    reviewRequired: { ref: string; name: string; groupedBy: string } | null;
    reviewFrequencyUnit: { id: string; text: string } | null;
    reviewFrequencyPeriod: number | null;
};

interface RiskReviewFieldSelectorProps {
    value?: ReviewFieldsType;
    onChange?: ((value: ReviewFieldsType) => void) | undefined;
    disabled?: boolean;
    hideReviewRequired?: boolean;
    'data-testid': string;
}

const RiskReviewSelector = ({
    value,
    onChange,
    disabled,
    hideReviewRequired = false,
    'data-testid': dataTestId,
}: RiskReviewFieldSelectorProps) => {
    const columnRef = useRef<HTMLDivElement>(null);
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'risks.dialogs.addRisk' });

    const booleanOptions: { ref: string; name: string; groupedBy: string }[] = [
        { ref: 'True', name: tRoot('bulkUpdateDialog.true'), groupedBy: '' },
        { ref: 'False', name: tRoot('bulkUpdateDialog.false'), groupedBy: '' },
    ];

    const trueOption = booleanOptions.find((x) => x.ref === 'True') ?? { ref: 'True', name: 'True', groupedBy: '' };

    const reviewFrequencyUnits: { id: string; text: string }[] = Object.assign([], RiskService.timeFrequencyListObject);
    const filteredReviewFrequencyUnits = hideReviewRequired
        ? reviewFrequencyUnits.filter((x) => x.id !== 'NONE')
        : reviewFrequencyUnits;

    useLayoutEffect(() => {
        columnRef.current?.setAttribute('data-testid', dataTestId);
        return () => {
            columnRef.current = null;
        };
    }, [dataTestId]);

    const { control, resetField } = useForm<ReviewFieldsType>({
        mode: 'all',
        defaultValues: {
            reviewFrequencyUnit: value ? value.reviewFrequencyUnit : null,
            reviewFrequencyPeriod: value ? value.reviewFrequencyPeriod : null,
            reviewRequired: hideReviewRequired ? trueOption : value ? value.reviewRequired : null,
        },
    });

    const reviewFrequencyPeriod = useWatch({ control, name: 'reviewFrequencyPeriod' });
    const reviewFrequencyUnit = useWatch({ control, name: 'reviewFrequencyUnit' });
    const reviewRequired = useWatch({ control, name: 'reviewRequired' });

    const effectiveReviewRequired = hideReviewRequired ? trueOption : reviewRequired;

    useEffect(() => {
        if (!value) {
            if (hideReviewRequired) {
                resetField('reviewRequired', { defaultValue: trueOption });
            }
            return;
        }

        if (hideReviewRequired) {
            resetField('reviewRequired', { defaultValue: trueOption });
        } else if (
            effectiveReviewRequired &&
            value?.reviewRequired &&
            effectiveReviewRequired.ref !== value?.reviewRequired.ref
        ) {
            resetField('reviewRequired', { defaultValue: value?.reviewRequired });
        }

        if (reviewFrequencyPeriod !== value?.reviewFrequencyPeriod) {
            resetField('reviewFrequencyPeriod', { defaultValue: value?.reviewFrequencyPeriod });
        }

        if (
            (reviewFrequencyUnit &&
                value.reviewFrequencyUnit &&
                reviewFrequencyUnit.id !== value.reviewFrequencyUnit.id) ||
            (reviewFrequencyUnit && !value.reviewFrequencyUnit)
        ) {
            resetField('reviewFrequencyUnit', { defaultValue: value?.reviewFrequencyUnit });
        }
    }, [value, hideReviewRequired]);

    const frequencyRequired = hideReviewRequired ? true : effectiveReviewRequired?.ref === 'True';
    const frequencyReadOnly = hideReviewRequired
        ? false
        : !(effectiveReviewRequired && effectiveReviewRequired.ref === 'True');

    return (
        <form id="bulk-update-risk-review-fields" className="k-w-full k-h-full">
            <div className={cn('k-pt-1', reviewStyles.riskConfigurationContainer)} ref={columnRef}>
                {!hideReviewRequired && (
                    <div className={cn('k-shrink-0 k-d-flex k-align-items-center k-ml-2 k-mr-2')}>
                        <span style={{ flexShrink: 0 }} className="k-label">
                            {t('fields.reviewRequired')}
                        </span>
                        <div className="k-ml-auto">
                            <BasicDropDownController
                                control={control}
                                name="reviewRequired"
                                data={booleanOptions ?? []}
                                data-testid="risk-field-review-required"
                                textField="name"
                                dataItemKey="ref"
                                fillMode="solid"
                                filterable
                                required
                                allowClear
                                className={cn(reviewStyles.reviewRequired)}
                                handleInputChange={(val) => {
                                    if (!disabled && onChange) {
                                        if (val.value?.ref === 'True')
                                            onChange({
                                                reviewFrequencyUnit: null,
                                                reviewFrequencyPeriod: null,
                                                reviewRequired: val.value,
                                            });
                                        else {
                                            resetField('reviewFrequencyUnit', {
                                                defaultValue: { id: 'NONE', text: 'None' },
                                            });
                                            onChange({
                                                reviewFrequencyUnit: { id: 'NONE', text: 'None' },
                                                reviewFrequencyPeriod: 0,
                                                reviewRequired: val.value,
                                            });
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}

                <div className={cn('k-shrink-0 k-d-flex k-justify-content-left k-ml-2 k-mr-2', styles.formFieldGap)}>
                    <div className="k-d-flex k-w-full k-mt-2 k-align-items-center">
                        <span className={cn('k-label')}>{tRoot('bulkUpdateDialog.frequency')}</span>
                        <div className="k-d-flex k-gap-2 k-ml-auto">
                            <div className={cn('!k-basis-1/2', reviewStyles.minWidthFix)}>
                                <NumericTextBoxController
                                    control={control}
                                    name="reviewFrequencyPeriod"
                                    data-testid="risk-field-review-frequency-period"
                                    required={frequencyRequired}
                                    readOnly={frequencyReadOnly}
                                    validityStyles={frequencyRequired}
                                    className={cn(reviewStyles.reviewFrequencyPeriod)}
                                    min={frequencyRequired ? RiskService.reviewFrequencyNumberMin : undefined}
                                    max={frequencyRequired ? RiskService.reviewFrequencyNumberMax : undefined}
                                    format="0"
                                    handleInputChange={(val) => {
                                        if (!disabled && onChange) {
                                            onChange({
                                                reviewFrequencyUnit,
                                                reviewFrequencyPeriod: val.value,
                                                reviewRequired: hideReviewRequired
                                                    ? trueOption
                                                    : effectiveReviewRequired,
                                            });
                                        }
                                    }}
                                />
                            </div>
                            <div className={cn('!k-basis-1/2', reviewStyles.minWidthFix, reviewStyles.dropdownFix)}>
                                <BasicDropDownController
                                    control={control}
                                    name="reviewFrequencyUnit"
                                    data-testid="risk-field-review-frequency-unit"
                                    textField="text"
                                    dataItemKey="id"
                                    filterable
                                    allowClear
                                    data={filteredReviewFrequencyUnits ?? []}
                                    required={frequencyRequired}
                                    readOnly={frequencyReadOnly}
                                    validityStyles={frequencyRequired}
                                    className={cn(reviewStyles.reviewFrequencyUnits)}
                                    handleInputChange={(val) => {
                                        if (!disabled && onChange) {
                                            onChange({
                                                reviewFrequencyUnit: val.value,
                                                reviewFrequencyPeriod,
                                                reviewRequired: hideReviewRequired
                                                    ? trueOption
                                                    : effectiveReviewRequired,
                                            });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

interface RiskReviewFieldControllerProps<TData, TControl extends FieldValues> {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (value: ReviewFieldsType) => void;
    disabled?: boolean;
    required?: boolean;
    hideReviewRequired?: boolean;
    'data-testid': string;
}

const RiskReviewFieldController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled,
    hideReviewRequired = false,
    'data-testid': dataTestId,
    required = false,
    validateRules,
    handleInputChange,
}: RiskReviewFieldControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value } }) => {
                return (
                    <RiskReviewSelector
                        value={value}
                        onChange={(val) => {
                            onChange(val);
                            if (handleInputChange) handleInputChange(val);
                        }}
                        disabled={disabled}
                        hideReviewRequired={hideReviewRequired}
                        data-testid={dataTestId}
                    />
                );
            }}
        />
    );
};

export { RiskReviewFieldController };
